import { useEffect, useState } from "react"
import Grid from "@mui/material/Grid"
import { GraphDto } from "@custom-types/graphs"
import { GraphEditMode } from "@features/graph/graphViewModel"
import { useAtomValue } from "jotai"
import { isModifiedAtom } from "@features/graph/state/graphAtoms"
import { useSearchParams } from "react-router"
import ScenarioTree, { DefaultSelection } from "@components/ScenarioTree"
import GraphActionBar from "@features/scenarioDesign/GraphActionBar"
import MainContent from "@features/scenarioDesign/MainContent"
import RunGroupDialog from "@features/run/RunGroupDialog"
import { enqueueSnackbar } from "notistack"
import RunGroupForm, { RunGroupFormValues } from "@features/run/RunGroupDialog/RunGroupForm"
import { usePostRunGroupMutation } from "@store/services/runService"
import { format } from "date-fns"
import {
  deleteGraphFromIndexedDb,
  graphGetIndexedDb,
  graphGetIndexedDbData,
  saveLocalGraph,
} from "@features/graph/state/indexedDBHelpers"
import { base64ToBlob } from "base64-blob"
import { downloadFile } from "utils/file"
import { RunGroupError } from "@features/scenarioDesign/RunTable/RunErrorDialog"
import { Viewport } from "reactflow"
import logger from "@utils/logger"
import { usePostGraphParametersMutation } from "@store/services/graphsService"

export enum PageMode {
  Start = "start",
  CreateScenario = "scenario",
  ViewRuns = "runs",
}

const FORM_ID = "scenario-design-page-run-group-form"

const ScenarioDesignPage = () => {
  const [searchParams, setSearchParams] = useSearchParams({})
  const mode = (searchParams.get("mode") as PageMode | null) ?? PageMode.Start

  const [selectedGraph, setSelectedGraph] = useState<GraphDto | undefined>(undefined)
  const [familyKey, setFamilyKey] = useState("")
  const [defaultSelection, setDefaultSelection] = useState<DefaultSelection>()
  const [editMode, setEditMode] = useState<GraphEditMode>(
    mode === PageMode.CreateScenario ? GraphEditMode.SCENARIO : GraphEditMode.OFF,
  )
  const [isRunDialogOpen, setIsRunDialogOpen] = useState(false)
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false)
  const [runGroupToHighlightKey, setRunGroupToHighlightKey] = useState<string | undefined>(
    undefined,
  )
  const [viewPort, setViewPort] = useState<Viewport | undefined>(undefined)
  const isModified = useAtomValue(isModifiedAtom)

  const [
    postRunGroup,
    {
      isError: isRunError,
      isSuccess: isRunSuccess,
      isLoading: isRunLoading,
      data: postRunGroupResponse,
      error: runGroupError,
    },
  ] = usePostRunGroupMutation()

  const [postExportGraphParameters] = usePostGraphParametersMutation()

  useEffect(() => {
    let errorMessage = "Failed to create run group"
    if (runGroupError && "status" in runGroupError) {
      const runError = runGroupError as RunGroupError
      errorMessage = runError.data?.detail?.error ?? errorMessage
    }

    if (isRunError) {
      enqueueSnackbar(errorMessage, { variant: "error" })
    }
    if (isRunSuccess) {
      enqueueSnackbar("Run group was created successfully", { variant: "success" })
    }

    setIsRunDialogOpen(isRunLoading)

    if (isRunSuccess) {
      setSearchParams((params) => {
        params.set("mode", PageMode.ViewRuns)

        return params
      })
      setRunGroupToHighlightKey(postRunGroupResponse?.key)
      setTimeout(() => setRunGroupToHighlightKey(undefined), 5000) // clear selection after 5 seconds

      // after creating a run, next time user expects to see original graph
      selectedGraph && deleteGraphFromIndexedDb(selectedGraph.key)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
  }, [isRunError, isRunSuccess, isRunLoading])

  // on first render check url params so we can expand correct nodes in scenario tree
  // and pre-select correct base graph version in action bar (at the end resulting fetching correct base graph)
  useEffect(() => {
    const graphKeyParam = searchParams.get("graphKey")
    if (!graphKeyParam) {
      return
    }

    const [organisationKey, divisionKey, productKey, familyKeyWithVersion] =
      decodeURIComponent(graphKeyParam).split("#")

    const [familyKey, version] = (familyKeyWithVersion || "_").split("_")
    setDefaultSelection({
      // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
      organisationKey,
      // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
      divisionKey,
      // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
      productKey,
      // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
      familyKey,
      // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
      version,
    })

    return () => setDefaultSelection(undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
  }, [])

  useEffect(() => {
    if (selectedGraph) {
      setSearchParams((params) => {
        params.set("graphKey", selectedGraph.key)

        return params
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
  }, [selectedGraph])

  const handleFamilySelected = (familyKey: string, causedByDefaultSelection: boolean) => {
    setFamilyKey(familyKey)
    setEditMode(mode === PageMode.CreateScenario ? GraphEditMode.SCENARIO : GraphEditMode.OFF)

    // if user was on different mode and refreshed page or got direct link
    // we don't want to switch the mode back to `Start`
    if (causedByDefaultSelection) {
      return
    }

    setSearchParams((params) => {
      params.set("mode", PageMode.Start)
      params.set("categoryId", "root")

      return params
    })
  }

  const handleBaseGraphChange = async (baseGraph: GraphDto) => {
    setViewPort(undefined)
    if (mode === PageMode.CreateScenario && !(await graphGetIndexedDb(baseGraph.key))) {
      await saveLocalGraph(baseGraph.key, baseGraph.s3_hash)
    }
    setSelectedGraph(baseGraph)
  }

  const handleRunGroupFormSubmit = async (values: RunGroupFormValues) => {
    if (!selectedGraph) {
      return
    }

    const graphZip = await graphGetIndexedDb(selectedGraph.key)

    if (!graphZip) {
      logger.error(`Missing local version of graph "${selectedGraph.key}"`)
      return
    }
    base64ToBlob(graphZip.base64).then((decoded) => {
      postRunGroup({
        ...values,
        base_graph_key: selectedGraph.key,
        graph_file_blob: isModified ? decoded : undefined,
        description: values.description ?? "",
        scenarios: values.scenarios ?? [],
        contract_key: values.contract_key ?? undefined,
        end_date:
          values.end_date instanceof Date ? format(values.end_date, "yyyy-MM-dd") : values.end_date,
        validate: values.validate,
        sampling_engine: "EVIEWS",
      })
    })
  }

  const handleSampleClick = async () => {
    if (!selectedGraph) {
      return
    }
    const graphData = await graphGetIndexedDbData(selectedGraph.key)
    if (!graphData) {
      return
    }

    setIsRunDialogOpen(true)

    if (!graphData.modified) {
      enqueueSnackbar("There are no changes made to the original graph", { variant: "info" })
    }
  }

  const handleModeChange = async (newMode: PageMode) => {
    setEditMode(newMode === PageMode.CreateScenario ? GraphEditMode.SCENARIO : GraphEditMode.OFF)
    if (
      newMode === PageMode.CreateScenario &&
      selectedGraph &&
      !(await graphGetIndexedDb(selectedGraph.key))
    ) {
      await saveLocalGraph(selectedGraph.key, selectedGraph.s3_hash)
    }
    setSearchParams((params) => {
      params.set("mode", newMode)

      return params
    })
  }

  const handleCloseImportParametersDialog = () => {
    setIsImportDialogOpen(false)
    // trigger react to refresh graph and fetch data from localstorage
    if (!selectedGraph) {
      return
    }
    setSelectedGraph({ ...selectedGraph })
  }

  const handleViewportChange = (v: Viewport) => {
    setViewPort(v)
  }

  const handleExportParameters = async () => {
    if (!selectedGraph) {
      return
    }

    const graphBase64 = await graphGetIndexedDb(selectedGraph.key)
    if (!graphBase64) {
      return
    }

    const fileBlob = await base64ToBlob(graphBase64.base64)

    try {
      const fileBase64 = await postExportGraphParameters({ fileBlob: fileBlob }).unwrap()
      downloadFile(`parameters_${selectedGraph.name}.xlsx`, fileBase64)
      enqueueSnackbar("Graph parameters were successfully exported", { variant: "success" })
    } catch (error) {
      enqueueSnackbar("Exporting parameters failed", { variant: "error" })
    }
  }

  return (
    <>
      <Grid container sx={{ height: "calc(100vh - 64px)", pl: 0 }} wrap="nowrap" gap={1}>
        <Grid item xs="auto">
          <ScenarioTree
            selectedFamilyKey={familyKey}
            onProductFamilySelect={handleFamilySelected}
            defaultSelection={defaultSelection}
            roleHint={"whatif"}
          />
        </Grid>

        <Grid container item direction="column" xs gap={1} wrap="nowrap">
          <Grid item>
            <GraphActionBar
              familyKey={familyKey}
              onBaseGraphChange={handleBaseGraphChange}
              defaultSelection={defaultSelection}
              mode={mode}
              onChangeMode={handleModeChange}
              createScenarioModeButtonsProps={{
                graphInfo: selectedGraph,
                familyKey: familyKey,
                editMode,
                onEdit: (value: GraphEditMode) => {
                  setEditMode(value)
                },
                onImportParameters: () => setIsImportDialogOpen(true),
                onExportParameters: handleExportParameters,
                onSample: handleSampleClick,
                onValidate: () => {},
              }}
              viewRunsModeButtonsProps={{
                onViewBaseGraph: () => {},
              }}
            />
          </Grid>
          <MainContent
            mode={mode}
            createScenarioModeProps={{
              editMode,
              viewPort,
              onViewPortChange: handleViewportChange,
              graph: selectedGraph,
              isImportParametersDialogOpen: isImportDialogOpen,
              onCloseImportParametersDialog: handleCloseImportParametersDialog,
            }}
            viewRunsModeProps={{
              baseGraphKey: selectedGraph?.key,
              runGroupToHighlightKey,
            }}
          />
        </Grid>
      </Grid>
      {selectedGraph && (
        <RunGroupDialog
          open={isRunDialogOpen}
          isSubmitting={isRunLoading}
          onClose={() => setIsRunDialogOpen(false)}
          formId={FORM_ID}
        >
          <RunGroupForm id={FORM_ID} graph={selectedGraph} onSubmit={handleRunGroupFormSubmit}>
            <RunGroupForm.Name />
            <RunGroupForm.NumberOfSamplings />
            <RunGroupForm.Seed />
            <RunGroupForm.ContractKey />
            <RunGroupForm.StartDate />
            <RunGroupForm.EndDate />
            <RunGroupForm.Scenario />
            <RunGroupForm.Description />
            <RunGroupForm.Validate />
          </RunGroupForm>
        </RunGroupDialog>
      )}
    </>
  )
}

export default ScenarioDesignPage
